import React, { useState } from "react";
import Link from "next/link";
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import CourieButton from "@src/common/components/Button/Button";
import { RouteStatusBadges } from "@src/routes/monitoring/components/RemoteLiveRouteListItem/components/RouteStatusBadges";
import { RoutesAttentionList } from "./RoutesAttentionList";
import { useRoutesAttention } from "./useRoutesAttention";

export function RoutesAttentionBanner() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { routes, summary, routesPageHref, isLoading, isStale } =
    useRoutesAttention();

  if (isLoading || summary.routeCount === 0) return null;

  // Per Temuge: show the breakdown rather than one "needs attention" number,
  // since users differ on whether At Risk is worth opening the route for.
  const counts = [
    {
      count: summary.lateCount,
      label: "Late",
      className: "text-rose-700 dark:text-rose-300",
    },
    {
      count: summary.atRiskCount,
      label: "At Risk",
      className: "text-amber-700 dark:text-amber-300",
    },
    {
      count: summary.unassignedCount,
      label: "Unassigned",
      className: "text-gray-700 dark:text-gray-300",
    },
  ].filter((item) => item.count > 0);

  return (
    <div className="border-t border-amber-200 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-900/20">
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
          <span className="flex items-center gap-2 font-semibold">
            <span className="h-2 w-2 flex-shrink-0 rounded-full bg-rose-500" />
            {counts.map((item, index) => (
              <React.Fragment key={item.label}>
                {index > 0 && (
                  <span className="text-amber-400/70 dark:text-amber-600/70">
                    ·
                  </span>
                )}
                <span className={item.className}>
                  {item.count} {item.label}
                </span>
              </React.Fragment>
            ))}
          </span>
          {summary.headlineRoute && (
            <>
              <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                {summary.headlineRoute.routeTemplate.name}
              </span>
              <RouteStatusBadges statuses={summary.headlineRoute.statusList} />
            </>
          )}
          {isStale && (
            <span className="text-xs text-amber-700 dark:text-amber-300">
              Couldn&apos;t refresh, showing last known status
            </span>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Link
            href={routesPageHref}
            className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            Go to routes
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </Link>
          <CourieButton
            size="xs"
            color="secondary"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
          >
            {isExpanded ? "Collapse" : "Expand"}
            {isExpanded ? (
              <ChevronDownIcon className="ml-1 h-3.5 w-3.5" />
            ) : (
              <ChevronUpIcon className="ml-1 h-3.5 w-3.5" />
            )}
          </CourieButton>
        </div>
      </div>
      {isExpanded && (
        <RoutesAttentionList routes={routes} routesPageHref={routesPageHref} />
      )}
    </div>
  );
}
